/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 31-08-2026
 *
 * De sessie van de ingelogde gebruiker (golf a1): in- en uitloggen, aanmelden met een
 * projectcode, de lesdirect-login, de wachtwoordlink, het overstappen naar een ander project —
 * en de userdata die daar uitkomt, als signals.
 *
 * Dit vervangt `api.userdata` uit AngularJS. De protocolvorm (`userdata.projectdata.<kolom>`)
 * blijft in `Userdata`/`Projectdata` staan, maar wie iets wil weten leest hier een computed met
 * een Nederlandse naam; zo staat de kennis van de backendvorm op één plek.
 */
import {computed, inject, Injectable, signal} from '@angular/core';
import {Sihwlog} from 'sihw-ngx';
import {ApiService} from '../app/backend/api.service';
import type {AanmeldResultaat, LoginGegevens, LoginResultaat, Projectdata, SwitchProject, Userdata} from '../app/backend/userdata.types';

@Injectable({providedIn: 'root'})
export class SessieService {

    private readonly log = new Sihwlog('info', 'SessieService');

    /**
     * De backendlaag; de sessie-acties zijn getypeerde calls daarop.
     */
    private readonly api = inject(ApiService);

    /**
     * De sessiegegevens van de ingelogde gebruiker; `null` als er niemand is ingelogd.
     */
    private readonly _userdata = signal<Userdata | null>(null);

    /**
     * Loopt er al een relogin? Voorkomt dat gelijktijdige aanroepers er elk één starten.
     */
    private reloginBezig: Promise<boolean> | null = null;

    /* ******** De sessie ******** */

    /**
     * De sessiegegevens van de ingelogde gebruiker.
     */
    readonly userdata = this._userdata.asReadonly();

    /**
     * Is er iemand ingelogd (voor zover deze client weet)?
     */
    readonly ingelogd = computed(() => !!this._userdata());

    /**
     * De gebruikersnaam waarmee is ingelogd.
     */
    readonly naam = computed(() => this._userdata()?.naam ?? '');

    /**
     * De weergavenaam van de ingelogde gebruiker.
     */
    readonly displaynaam = computed(() => this._userdata()?.displayname ?? '');

    /**
     * Het domein waar de gebruiker onder valt.
     */
    readonly userdomein = computed(() => this._userdata()?.userdomein ?? null);

    /**
     * Mag deze gebruiker met andermans modellen meekijken?
     */
    readonly magMeekijken = computed(() => !!this._userdata()?.meekijker);

    /**
     * Mag deze gebruiker het eerste docentendashboard openen?
     */
    readonly heeftDashboard1 = computed(() => !!this._userdata()?.dashboard1);

    /**
     * Mag deze gebruiker het tweede docentendashboard openen?
     */
    readonly heeftDashboard2 = computed(() => !!this._userdata()?.dashboard2);

    /**
     * Mag deze gebruiker een replay draaien?
     */
    readonly magReplay = computed(() => !!this._userdata()?.replay);

    /**
     * De maximale simulatieduur voor deze gebruiker in dit project, in seconden.
     */
    readonly simtimeout = computed(() => this._userdata()?.simtimeout ?? null);

    /**
     * Het aantal modellen van deze gebruiker in dit project.
     */
    readonly aantalModellen = computed(() => this._userdata()?.nummodellen ?? 0);

    /**
     * Het id van het enige model van deze gebruiker, als hij er precies één heeft.
     */
    readonly enkelModel = computed(() => this._userdata()?.enkelmodel ?? null);

    /**
     * De projecten waarheen overgestapt kan worden; leeg als dat er niet meer dan één is.
     */
    readonly switchProjecten = computed<SwitchProject[]>(() => this._userdata()?.switchprojecten ?? []);

    /**
     * Mag deze gebruiker naar een ander project overstappen? Het backend levert de lijst alleen
     * als er meer dan één project is.
     */
    readonly magSwitchProject = computed(() => this.switchProjecten().length > 0);

    /* ******** Het project ******** */

    /**
     * De projectgegevens van het project waarop is ingelogd.
     */
    readonly projectdata = computed<Projectdata | null>(() => this._userdata()?.projectdata ?? null);

    /**
     * Het id van het huidige project.
     */
    readonly projectId = computed(() => this.projectdata()?.id ?? null);

    /**
     * De code van het huidige project, zoals getoond voor de modelnaam.
     */
    readonly projectcode = computed(() => this.projectdata()?.code ?? '');

    /**
     * De domeinnaam van het huidige project.
     */
    readonly domeinnaam = computed(() => this.projectdata()?.domeinnaam ?? '');

    /**
     * Mag er in dit project samengewerkt worden?
     */
    readonly magSamenwerken = computed(() => !!this.projectdata()?.samenwerken);

    /**
     * Wanneer er in dit project gechat mag worden: `nooit`, `samenwerken` of `altijd`.
     */
    readonly chatmodus = computed(() => this.projectdata()?.chat ?? 'nooit');

    /**
     * Opent dit project met de keuzepopup?
     */
    readonly heeftStartpopup = computed(() => !!this.projectdata()?.popup);

    /**
     * Moet de applicatie na een nieuw model opnieuw opstarten?
     */
    readonly forceerStart = computed(() => !!this.projectdata()?.forceer_start);

    /**
     * Het model of normmodel dat bij het opstarten geopend wordt.
     */
    readonly standaardmodel = computed(() => this.projectdata()?.standaardmodel ?? null);

    /**
     * Of het standaardmodel een norm of een template is.
     */
    readonly standaardmodelType = computed(() => this.projectdata()?.standaardmodel_type ?? null);

    /**
     * Het modelleerniveau van het project.
     */
    readonly modelleerniveau = computed(() => this.projectdata()?.modelleerniveau ?? 0);

    /**
     * Start dit project met een instructievideo?
     */
    readonly heeftStartvideo = computed(() => !!this.projectdata()?.startvideo);

    /**
     * Mag de gebruiker het snelste-padgedrag zelf aan- en uitzetten?
     */
    readonly fastestPathOverschrijfbaar = computed(() => !!this.projectdata()?.fastest_path_overschrijfbaar);

    /**
     * Worden er snapshots bij de actielogregels bewaard? Voorwaarde voor replay.
     */
    readonly actionlogSnapshots = computed(() => !!this.projectdata()?.actionlog_snapshots);

    /* ******** Acties ******** */

    /**
     * Meldt de gebruiker aan. Het antwoord is óf een volledige login, óf de domein-/projectkeuze
     * die nog gemaakt moet worden. Bij een fout is de uitkomst `false`.
     */
    async login(gegevens: LoginGegevens): Promise<LoginResultaat | false> {
        this.logout(); //alles wat er stond eerst weg
        try {
            const res = await this.api.login(gegevens);
            if (!res.authkey) {
                res.loggedin = false;
                return res;
            }
            res.loggedin = true;
            this._userdata.set(res.userdata ?? null);
            this.api.authKey = res.authkey; //vanaf nu deze key
            delete res.authkey;
            return res;
        } catch (err) {
            this.log.error('Inloggen gaf error', err);
            this.logout(); //opschonen
            return false;
        }
    }

    /**
     * Meldt aan met een projectcode. Zonder bevestigingscode is dit de aanvraag; mét
     * bevestigingscode, wachtwoord en displaynaam wordt het account aangemaakt en meteen
     * ingelogd. Een fout is geen uitzondering maar een status: `ONGELDIG` als het backend de code of de
     * bevestiging weigert (`NO_AUTH`), en `FOUT` bij al het andere, zoals een timeout of een storing. Zo kan
     * het scherm de gebruiker zeggen wat er aan de hand is.
     */
    async aanmeldenCode(code: string, email: string, taal: string, bevestigingscode?: string,
                        password?: string, displaynaam?: string): Promise<AanmeldResultaat> {
        this.logout();
        try {
            const res = await this.api.aanmeldenCode(code, email, taal, bevestigingscode, password, displaynaam);
            if (res && res.status === 'ingelogd') {
                this.neemSessieOver(res);
            }
            return res;
        } catch (err) {
            this.log.error('Aanmelden code gaf error', err);
            this.logout();
            return {status: (err as {code?: string})?.code === 'NO_AUTH' ? 'ONGELDIG' : 'FOUT'};
        }
    }

    /**
     * Logt in met een lesdirect-sleutel uit material. Een mislukte poging valt door als reject,
     * zodat de aanroeper naar het loginscherm kan sturen.
     */
    async lesdirectLogin(key: string): Promise<AanmeldResultaat> {
        this.logout();
        const res = await this.api.lesdirectLogin(key);
        if (res && res.status === 'ingelogd') {
            this.neemSessieOver(res);
        }
        return res;
    }

    /**
     * Controleert of er is ingelogd. Is de userdata er al, dan geloven we dat; anders vraagt
     * deze methode het één keer aan het backend, dat meteen het token ververst. Gelijktijdige
     * aanroepers delen dezelfde vraag.
     */
    isIngelogd(): Promise<boolean> {
        if (this.reloginBezig) {
            return this.reloginBezig; //we zijn al bezig
        }
        if (!this.api.heeftAuth) {
            this.logout();
            return Promise.resolve(false);
        }
        if (this._userdata()) {
            return Promise.resolve(true); //al een keer gevraagd
        }
        this.reloginBezig = this.doeRelogin();
        return this.reloginBezig;
    }

    /**
     * Stapt over naar een ander project van dezelfde gebruiker. De aanroeper herlaadt daarna de
     * pagina, zodat alles op de nieuwe userdata opnieuw begint.
     */
    async switchProject(projectId: number | string): Promise<boolean> {
        try {
            const res = await this.api.switchProject(projectId);
            if (!(res.authkey && res.userdata)) {
                this.log.error('switchProject mislukt', res);
                return false;
            }
            this.log.debug('switchProject toegestaan', res);
            this._userdata.set(res.userdata);
            this.api.authKey = res.authkey;
            return true;
        } catch (e) {
            this.log.error(e);
            return false;
        }
    }

    /**
     * Logt uit: de authkey en de sessiegegevens gaan weg.
     */
    logout(): true {
        this.api.verwijderAuth();
        this._userdata.set(null);
        return true;
    }

    /**
     * Laat het backend een wachtwoordlink mailen naar dit adres.
     * @param email het adres van de gebruiker
     * @param taal de taal waarin de mail gesteld wordt
     */
    stuurWachtwoordlink(email: string, taal: string): Promise<any> {
        return this.api.stuurWachtwoordlink(email, `${location.protocol}//${location.host}/#!/ww`, taal);
    }

    /**
     * Is deze wachtwoordlink nog geldig?
     */
    isWachtwoordlink(token: string): Promise<boolean> {
        return this.api.isWachtwoordlink(token);
    }

    /**
     * Zet een nieuw wachtwoord op basis van een geldige wachtwoordlink.
     */
    updateWachtwoord(token: string, nieuwWachtwoord: string): Promise<boolean> {
        return this.api.updateWachtwoord(token, nieuwWachtwoord);
    }

    /* ******** Hulpmethoden ******** */

    /**
     * Neemt de sessie over uit een antwoord dat een authkey en userdata bevat.
     */
    private neemSessieOver(res: { authkey?: string; userdata?: Userdata }): void {
        this._userdata.set(res.userdata ?? null);
        if (res.authkey) {
            this.api.authKey = res.authkey;
        }
    }

    /**
     * Vraagt het backend om een verse sessie op basis van de opgeslagen authkey. Ruimt de
     * lopende-vraag-markering altijd op, zodat een volgende aanroep opnieuw mag proberen.
     */
    private async doeRelogin(): Promise<boolean> {
        try {
            const res = await this.api.relogin();
            this.log.debug('isIngelogd: userdata gevonden', res);
            this.neemSessieOver(res);
            return true;
        } catch (err) {
            this.log.error(err);
            this.logout();
            return false;
        } finally {
            this.reloginBezig = null;
        }
    }
}
